import { FarmSlotType } from 'System/Farm/Data/FarmSlotType';
import { FarmItemUnlockType } from 'System/Farm/Data/FarmItemUnlockType';
import { cached } from 'Utility/async';
import { Layout } from 'Utility/layout/createLayout';
import { FarmCatalogueTab } from 'System/UI/Menu/FarmCatalogueTab';
import { FARM_ITEMS_DATA } from '../../../../build/temp/FarmItemData';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { FieldToWorldMap } from '../../../../build/temp/WorldFieldMap';
import { report } from 'report';

export type FarmItemData = {
    itemId: FarmItemId;
    farmItemType: FarmItemType;
    farmItemUnlockType: FarmItemUnlockType;
    layout: Layout;
    slotType: FarmSlotType;
    tab: FarmCatalogueTab;
    tabOrder: number;
    productId?: string;
    layoutAssets: string[];
} & (
    | {
          farmItemUnlockType: FarmItemUnlockType.PREMIUM;
      }
    | {
          farmItemUnlockType: FarmItemUnlockType.CROP;
          fieldId: string;
      }
    | {
          farmItemUnlockType: FarmItemUnlockType.WORLD;
          world: number;
      }
);

export enum FarmItemType {
    SMALL = 'small',
    BIG = 'big',
    TREE = 'tree',
    CROP = 'crop',
    FENCE = 'fence',
    HOUSE = 'house',
}

export const FARM_CROP_ITEM_DATA = (FARM_ITEMS_DATA as unknown) as { [key: string]: FarmItemData };

export function getFarmItemLayout(farmItemId: FarmItemId): GLayout<any> {
    return new FARM_CROP_ITEM_DATA[farmItemId].layout();
}

export function getFarmItemById(farmItemId: FarmItemId) {
    const item = FARM_CROP_ITEM_DATA[farmItemId];

    if (!item) {
        report('Failed to get farm item by id.', {
            once: true,
            sampleRate: 0.1,
            tags: {
                farmItemId,
            },
            fingerprint: 'FailedToGetFarmItemById:getFarmItemById',
        });
    }

    return item;
}

export function getFarmItemWorldNumber(farmItemId: FarmItemId): number | undefined {
    const itemData = getFarmItemById(farmItemId);

    if (itemData.farmItemUnlockType === FarmItemUnlockType.WORLD) {
        return itemData.world;
    } else if (itemData.farmItemUnlockType === FarmItemUnlockType.CROP) {
        return FieldToWorldMap.get(itemData.fieldId);
    } else {
        return;
    }
}

export const CACHED_FARM_ITEM_DATA = cached<FarmItemData[]>(() => {
    return Object.values(FARM_CROP_ITEM_DATA);
});

export type FarmItemId = Exclude<keyof typeof FARM_CROP_ITEM_DATA, number>;

export const CACHED_FARM_ITEM_IDS = cached<FarmItemId[]>(() => {
    return CACHED_FARM_ITEM_DATA().map(item => item.itemId);
});

export function isFarmItemId(value: unknown): value is FarmItemId {
    return CACHED_FARM_ITEM_IDS().includes(value as FarmItemId);
}
